import React, { useState, useRef } from 'react';
import { Check, ArrowRight, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { track } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const f = new FormData(formRef.current);
    const payload = {
      name: (f.get('name') || '').toString().trim(),
      email: (f.get('email') || '').toString().trim(),
      company: (f.get('company') || '').toString().trim(),
      role: (f.get('role') || '').toString().trim(),
      phone: (f.get('phone') || '').toString().trim(),
      country: (f.get('country') || '').toString().trim(),
      message: (f.get('message') || '').toString().trim(),
      timeline: (f.get('timeline') || '').toString(),
      budget: (f.get('budget') || '').toString(),
    };
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
      track('Enquiry Submitted', {
        company: payload.company || '(none)',
        timeline: payload.timeline || '(unset)',
        budget: payload.budget || '(unset)',
      });
    } catch (err) {
      setError('We could not send that just now. Please retry, or email connect@digitalworks.co directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact — book a 60-minute working session"
        description="Sixty minutes with two of our engineers. Bring a delivery problem you have not solved. Leave with a written assessment of where AI would help, where it would not, and what finding out would involve."
        canonical="https://digitalworks.co/contact"
      />
      <section className="pt-16 pb-10">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Contact</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4">Book a working session.</h1>
          <p className="mt-10 max-w-3xl text-[17px] leading-relaxed text-[#1A1A1A]/85">Sixty minutes with two of our engineers. Bring a delivery problem you have not solved. You will leave with a written assessment of where AI would help, where it would not, and what finding out would involve. If we do not think we are the right partner, we will say so in the session rather than in a follow-up email.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="dwx grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-7">
            {submitted ? (
              <div
                className="rounded-[20px] bg-[#22A94F]/12 border border-[#22A94F]/30 p-10"
                data-testid="contact-submitted"
                role="status"
                aria-live="polite"
              >
                <Check size={30} className="text-[#178838]" />
                <div className="serif text-[32px] mt-4">We have it.</div>
                <p className="mt-3 text-[15px] text-[#1A1A1A]/80">You will hear from a person within one working day.</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input required name="name" placeholder="Name" data-testid="contact-name" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <input required name="email" type="email" placeholder="Work email" data-testid="contact-email" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <input name="company" placeholder="Company" data-testid="contact-company" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <input name="role" placeholder="Role" data-testid="contact-role" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <input name="phone" type="tel" placeholder="Phone (optional)" data-testid="contact-phone" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <input name="country" placeholder="Country (optional)" data-testid="contact-country" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
                <textarea name="message" placeholder="What are you trying to solve?" data-testid="contact-message" className="md:col-span-2 px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px] min-h-[140px]" />
                <select name="timeline" data-testid="contact-timeline" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]">
                  <option value="Exploring">Timeline — exploring</option>
                  <option value="0–3 months">0–3 months</option>
                  <option value="3–6 months">3–6 months</option>
                  <option value="6–12 months">6–12 months</option>
                </select>
                <select name="budget" data-testid="contact-budget" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]">
                  <option value="To be discussed">Budget — to be discussed</option>
                  <option value="Under USD 250k">Under USD 250k</option>
                  <option value="USD 250k – 1M">USD 250k – 1M</option>
                  <option value="USD 1M+">USD 1M+</option>
                </select>
                {error && (
                  <div className="md:col-span-2 flex items-start gap-2 text-[13px] text-[#B4351A]" role="alert" data-testid="contact-error">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="md:col-span-2 justify-self-start inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FBFAF6] text-[14px] font-medium hover:bg-[#2B2B2B] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : (<>Book the session <ArrowRight size={16} /></>)}
                </button>
              </form>
            )}
          </div>
          <aside className="col-span-12 md:col-span-4 md:col-start-9 space-y-6">
            <div className="rounded-[20px] bg-[#F4F2EC] p-6 border border-[#1A1A1A]/10">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 mb-3">Direct</div>
              <a href="mailto:connect@digitalworks.co" className="text-[15px] link-sweep">connect@digitalworks.co</a>
              <div className="mt-4 text-[13px] text-[#1A1A1A]/60">We reply within one working day.</div>
            </div>
            <div className="rounded-[20px] bg-[#F4F2EC] p-6 border border-[#1A1A1A]/10">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 mb-3">Office</div>
              <ul className="space-y-2 text-[14px]">
                <li>Dubai, UAE</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
